import React, { useState, useEffect } from 'react';
import { getInbox } from '../../api/messagingApi';
import MessageItem from './MessageItem';
import './MessageInbox.css';

/**
 * MessageInbox - Main inbox display component
 *
 * @param {Object} props
 * @param {Function} props.onUnreadCountChange - Callback when unread count changes (optional)
 * @param {Function} props.onReportBug - Callback to open bug report modal (optional)
 */
function MessageInbox({ onUnreadCountChange, onReportBug }) {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'unread', 'read'
    const [unreadCount, setUnreadCount] = useState(0);

    // Fetch inbox messages
    const fetchInbox = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const params = {
                includeBroadcasts: true,
                limit: 100
            };

            // Apply status filter
            if (statusFilter !== 'all') {
                params.status = statusFilter.toUpperCase();
            }

            const data = await getInbox(params);
            setMessages(data.messages || []);
            setUnreadCount(data.unread_count || 0);

            // Notify parent of unread count change
            if (onUnreadCountChange && typeof onUnreadCountChange === 'function') {
                onUnreadCountChange(data.unread_count || 0);
            }
        } catch (err) {
            console.error('MessageInbox: Error fetching inbox:', err);
            setError(err.message || 'Failed to load messages. Please try again.');
            setMessages([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch inbox on mount and when status filter changes
    useEffect(() => {
        fetchInbox();
    }, [statusFilter]);

    // Handle message update (e.g., marked as read)
    const handleMessageUpdated = (messageId, updates) => {
        setMessages(prevMessages =>
            prevMessages.map(msg =>
                msg.id === messageId ? { ...msg, ...updates } : msg
            )
        );

        // If status changed from unread to read, decrement unread count
        if (updates.status === 'READ') {
            const newUnreadCount = Math.max(0, unreadCount - 1);
            setUnreadCount(newUnreadCount);
            if (onUnreadCountChange && typeof onUnreadCountChange === 'function') {
                onUnreadCountChange(newUnreadCount);
            }
        }
    };

    // Handle message deletion
    const handleMessageDeleted = (messageId) => {
        // Find the message to check if it was unread
        const deletedMessage = messages.find(msg => msg.id === messageId);
        const wasUnread = deletedMessage && deletedMessage.status === 'UNREAD';

        // Remove from list
        setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));

        // Update unread count if needed
        if (wasUnread) {
            const newUnreadCount = Math.max(0, unreadCount - 1);
            setUnreadCount(newUnreadCount);
            if (onUnreadCountChange && typeof onUnreadCountChange === 'function') {
                onUnreadCountChange(newUnreadCount);
            }
        }
    };

    // Handle filter change
    const handleFilterChange = (filter) => {
        if (filter !== 'all' && filter !== 'unread' && filter !== 'read') {
            throw new Error(`MessageInbox: Invalid filter "${filter}" - must be "all", "unread", or "read"`);
        }
        setStatusFilter(filter);
    };

    return (
        <div className="message-inbox">
            {/* Filter Buttons and Bug Report Action */}
            <div className="inbox-header">
                <div className="inbox-filters">
                    <button
                        className={`filter-button ${statusFilter === 'all' ? 'filter-button-active' : ''}`}
                        onClick={() => handleFilterChange('all')}
                    >
                        All
                    </button>
                    <button
                        className={`filter-button ${statusFilter === 'unread' ? 'filter-button-active' : ''}`}
                        onClick={() => handleFilterChange('unread')}
                    >
                        Unread {unreadCount > 0 && `(${unreadCount})`}
                    </button>
                    <button
                        className={`filter-button ${statusFilter === 'read' ? 'filter-button-active' : ''}`}
                        onClick={() => handleFilterChange('read')}
                    >
                        Read
                    </button>
                </div>
                
                {/* Bug Report Button */}
                {onReportBug && (
                    <button
                        className="bug-report-action-button"
                        onClick={onReportBug}
                        title="Report a bug or issue"
                    >
                        🐛 Report a Bug
                    </button>
                )}
            </div>

            {/* Messages List */}
            <div className="inbox-messages">
                {isLoading ? (
                    <div className="inbox-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading messages...</p>
                    </div>
                ) : error ? (
                    <div className="inbox-error">
                        <p>{error}</p>
                        <button onClick={fetchInbox} className="retry-button">
                            Retry
                        </button>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="inbox-empty">
                        <div className="empty-icon">✉️</div>
                        <p className="empty-title">No messages yet</p>
                        <p className="empty-subtitle">
                            {statusFilter === 'unread'
                                ? 'All caught up! No unread messages.'
                                : statusFilter === 'read'
                                ? 'No read messages to display.'
                                : 'Messages from friends and system announcements will appear here.'}
                        </p>
                    </div>
                ) : (
                    messages.map((message) => (
                        <MessageItem
                            key={message.id}
                            message={message}
                            onMessageUpdated={handleMessageUpdated}
                            onMessageDeleted={handleMessageDeleted}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

export default MessageInbox;
